import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Switch,
  Spin,
  Tag,
  notification,
} from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloudOutlined,
  ApiOutlined,
  CustomerServiceOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import {
  fetchIntegrations,
  connectIntegration,
  disconnectIntegration,
  updateIntegration,
} from "./service";
import { SONIC_CRM_PROVIDERS } from "./constants";
import styles from "./style.module.scss";

// Salesforce, HubSpot and Zendesk connectors are not built for Sonic yet.
const COMING_SOON = true;

const PROVIDER_ICONS = {
  salesforce: CloudOutlined,
  hubspot: ApiOutlined,
  zendesk: CustomerServiceOutlined,
};

const IntegrationsTab = ({ active }) => {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyProvider, setBusyProvider] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchIntegrations();
    if (res && !res.error) {
      setIntegrations(res.integrations || []);
    } else {
      setIntegrations([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const byProvider = integrations.reduce((acc, row) => {
    acc[row.provider] = row;
    return acc;
  }, {});

  const handleConnect = async (provider) => {
    setBusyProvider(provider);
    const res = await connectIntegration(provider);
    setBusyProvider(null);
    if (res && !res.error) {
      notification.success({
        message: t("sonic.integration_connected", {
          provider: t(`sonic.integration_${provider}`),
        }),
      });
      load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleDisconnect = async (provider) => {
    setBusyProvider(provider);
    const res = await disconnectIntegration(provider);
    setBusyProvider(null);
    if (res && !res.error) {
      notification.success({
        message: t("sonic.integration_disconnected", {
          provider: t(`sonic.integration_${provider}`),
        }),
      });
      load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const handleCrmToggle = async (provider, checked) => {
    setBusyProvider(`${provider}-toggle`);
    const res = await updateIntegration(provider, { auto_create_crm_note: checked });
    setBusyProvider(null);
    if (res && !res.error) {
      load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  if (loading) {
    return (
      <div className={styles.centeredSpin}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.tabInner}>
      <div className={styles.sonicPanel}>
        <h3 className={styles.integrationsTitle}>{t("sonic.integrations_title")}</h3>
        <p className={styles.fieldHelper}>{t("sonic.integrations_desc")}</p>

        <div className={styles.integrationGrid}>
          {SONIC_CRM_PROVIDERS.map(({ key, labelKey }) => {
            const row = byProvider[key] || {};
            // No CRM connector exists yet: never show a provider as connected.
            const connected = COMING_SOON ? false : row.status === "connected" || row.connected;
            const Icon = PROVIDER_ICONS[key] || CloudOutlined;
            return (
              <div key={key} className={styles.integrationCard}>
                <div className={styles.integrationCardHeader}>
                  <Icon className={styles.integrationIcon} />
                  <div>
                    <div className={styles.integrationName}>{t(labelKey)}</div>
                    {COMING_SOON ? (
                      <Tag>{t("sonic.integration_coming_soon")}</Tag>
                    ) : (
                      <Tag
                        icon={
                          connected ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />
                        }
                        color={connected ? "green" : "orange"}
                      >
                        {connected ? t("sonic.connected") : t("sonic.not_connected")}
                      </Tag>
                    )}
                  </div>
                </div>
                <p className={styles.integrationDesc}>
                  {t(`sonic.integration_${key}_desc`)}
                </p>
                {connected && (
                  <div className={styles.toggleRow}>
                    <div className={styles.toggleDesc}>
                      {t("sonic.crm_auto_note_toggle")}
                    </div>
                    <Switch
                      checked={!!row.auto_create_crm_note}
                      loading={busyProvider === `${key}-toggle`}
                      onChange={(v) => handleCrmToggle(key, v)}
                    />
                  </div>
                )}
                <div className={styles.integrationActions}>
                  {connected ? (
                    <Button
                      danger
                      loading={busyProvider === key}
                      onClick={() => handleDisconnect(key)}
                    >
                      {t("sonic.disconnect")}
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      disabled={COMING_SOON}
                      loading={busyProvider === key}
                      onClick={() => handleConnect(key)}
                    >
                      {COMING_SOON ? t("sonic.integration_coming_soon") : t("sonic.connect_integration")}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsTab;
